import cx from 'classnames';
import type { FieldProps } from 'formik';
import { Field, FieldArray, Formik } from 'formik';
import type { MouseEvent, ReactNode } from 'react';
import { useContext, useEffect, useState } from 'react';
import { Accordion, Button, Card, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import * as Yup from 'yup';

import { createUserTrace } from '../../api/Optimise';
import modalAngle from '../../assets/svg/solar-modal-angle.svg';
import modalDirection from '../../assets/svg/solar-modal-direction.svg';
import modalGroup from '../../assets/svg/solar-modal-group.svg';
import modalNumber from '../../assets/svg/solar-modal-number.svg';
import { OnboardingContext } from '../../context/OnboardingContext';
import ErrorLabel from '../../ui/ErrorLabel';
import LoopSpinner from '../../ui/LoopSpinner';
import InfoModal from '../../ui/Modal/InfoModal';
import { min } from '../../utils/breakpoints';
import prepareArrays from '../../utils/prepareArrays';

const StyledFormHeader = styled.h4`
  color: ${(props) => props.theme.colors.themedFormHeaders};
`;

const StyledFormContainer = styled.div`
  margin-top: 2rem;
  text-align: left;
`;

const StyledAccordion = styled(Accordion)`
  margin-bottom: 1rem;
`;

const StyledAccordionToggle = styled(Accordion.Button)`
  width: 100%;
  background-color: ${(props) => (props.isEmpty ? '#F5F5F5 !important' : '#FFF !important')};
  display: flex;
  flex-direction: column;
  justify-content: stretch !important;
  align-items: flex-start !important;
  padding: 20px !important;
  border: none !important;
`;

const StyledAccordionToggleHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h4 {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 0;
  }
`;

const StyledChangeButton = styled(Button)`
  padding: 0 !important;
`;

const StyledCard = styled.div`
  border: 2px solid ${(props) => props.theme.colors.light} !important;
  margin-bottom: 1rem;
  box-shadow: none !important;
  border-radius: 4px !important;
`;

const StyledCardBody = styled(Card.Body)`
  padding: 1rem;
  background-color: white;
  border-top: 2px solid ${(props) => props.theme.colors.light} !important;
`;

const FieldLabelWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const StyledFormLabel = styled(Form.Label)`
  margin-bottom: 0;
`;

const InfoDiv = styled.div`
  cursor: pointer;
  padding-top: 1px;
`;

const StyledButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  outline: inherit;
  text-decoration: underline !important;
  color: ${(props) => props.theme.colors.primary};
  display: inline-block;
  font-size: 14px;

  ${min.sm} {
    font-size: 16px;
  }
`;

const StyledModalContent = styled.div`
  h3 {
    font-weight: bold;
    font-size: 22px;
    margin-bottom: 12px;
  }

  p {
    font-size: 16px;
    line-height: 1.5;
  }

  img {
    margin-top: 1rem;
    width: 100%;
  }
`;

interface Group {
  orientation: number | string;
  tilt: number | string;
  panelCount: number | string;
}

const SolarPanelGroupsContainer = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const {
    setProgressPercentage,
    solarPanelArrayCount,
    solarPanelArrayData,
    setSolarPanelArrayData,
    numPanels,
    setNumPanels,
    postcode,
  } = useContext(OnboardingContext);
  const [activeKey, setActiveKey] = useState('0');
  const nextRoute = '/onboarding/solar-diverter';
  const [showInfo, setShowInfo] = useState(false);
  const [infoContent, setInfoContent] = useState<ReactNode>(undefined);

  const orientationOptions: Record<number, string> = {
    0: 'North',
    45: 'Northeast',
    90: 'East',
    135: 'Southeast',
    180: 'South',
    225: 'Southwest',
    270: 'West',
    315: 'Northwest',
  };

  const tiltOptions: Record<number, string> = {
    0: '0°',
    20: '20°',
    40: '40°',
    60: '60°',
  };
  const validationSchema = Yup.object().shape({
    groups: Yup.array().of(
      Yup.object().shape({
        orientation: Yup.number().required('Please select an orientation'),
        tilt: Yup.number().required('Please select a tilt'),
        panelCount: Yup.number()
          .required('Please enter the number of panels')
          .min(1, 'Must have at least 1 panel')
          .max(100, 'Cannot exceed 100 panels'),
      })
    ),
  });

  const allGroupPanelsFilled = (groups: Group[]) =>
    groups.every((group) => {
      return group.panelCount;
    });

  const totalPanelsFilled = (groups: Group[]) =>
    groups.reduce((acc: number, group) => acc + (parseInt(String(group.panelCount)) || 0), 0);

  const totalPanelsDifferentToNumPanels = (groups: Group[]) =>
    allGroupPanelsFilled(groups) && !!numPanels && numPanels !== totalPanelsFilled(groups);

  /**
   * Prefill panel count when the user has a single panel group (all facing the same
   * direction) and previously told us how many panels they have.
   */
  const getInitialPanelCount = (index: number) => {
    const savedPanelCount = solarPanelArrayData?.[`group-${index + 1}`]?.panelCount;
    if (savedPanelCount) {
      return savedPanelCount;
    }

    const isSingleDirectionGroup = parseInt(solarPanelArrayCount, 10) === 1;
    if (isSingleDirectionGroup && numPanels) {
      return numPanels;
    }

    return '';
  };

  const initialValues = {
    groups: Array.from({ length: parseInt(solarPanelArrayCount) }, (_, index) => ({
      orientation: solarPanelArrayData?.[`group-${index + 1}`]?.orientation || '',
      tilt: solarPanelArrayData?.[`group-${index + 1}`]?.tilt || '',
      panelCount: getInitialPanelCount(index),
    })),
  };

  const onSubmit = async (values: { groups: Group[] }) => {
    if (totalPanelsDifferentToNumPanels(values.groups)) {
      setNumPanels(totalPanelsFilled(values.groups));
    }

    const newSolarPanelArrayData = values.groups.reduce((acc: Record<string, Group>, group, index: number) => {
      acc[`group-${index + 1}`] = group;
      return acc;
    }, {});

    setSolarPanelArrayData(newSolarPanelArrayData);
    let preparedArrays: unknown[] = [];
    try {
      // If ElectraLink is down, this call can fail
      preparedArrays = await prepareArrays(newSolarPanelArrayData, postcode);
    } catch (e) {
      console.error(e);
    }
    void createUserTrace('Solar Panels', {
      pv_details: {
        arrays: preparedArrays,
      },
    });
    void navigate(nextRoute);
  };

  useEffect(() => {
    setProgressPercentage(45);
  }, [setProgressPercentage]);

  const isGroupComplete = (group: Group) => {
    return group.orientation && group.tilt && group.panelCount;
  };

  const isGroupEmpty = (group: Group) => {
    return !group.orientation && !group.tilt && !group.panelCount;
  };

  const showInfoModal = (e: MouseEvent, info: ReactNode) => {
    e.stopPropagation();
    setInfoContent(info);
    setShowInfo(true);
  };

  return (
    <>
      <h1 className="mb-3">Your solar panels</h1>
      <div>
        <p>
          Please tell us the <strong>direction</strong>, <strong>angle</strong>, and <strong>number of panels</strong>{' '}
          in each group of panels, on each part of your roof.
        </p>
        <StyledButton
          onClick={(e) =>
            showInfoModal(
              e,
              <StyledModalContent>
                <h3>Group of solar panels</h3>
                <p>
                  A group of solar panels is all the panels on one part of your roof, all facing in the same direction.
                  Solar panels which are separated by a chimney or something else on the same part of the roof, are
                  still considered to be in the same group.
                </p>
                <p>
                  <strong>If two sets of panels are facing the same way, they are the same group.</strong>
                </p>
                <p>
                  In the example below, there are 2 groups of solar panels. One group facing west, and a second group
                  facing south. Each have 8 panels in total.
                </p>
                <img src={modalGroup} alt="Group of solar panels" />
              </StyledModalContent>
            )
          }
        >
          What is a group of solar panels?
        </StyledButton>
        <StyledFormContainer>
          <Formik
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            initialValues={initialValues}
            validateOnChange
          >
            {({ handleSubmit, isValid, isSubmitting, values }) => (
              <Form onSubmit={handleSubmit}>
                <FieldArray name="groups">
                  {() => (
                    <StyledAccordion
                      activeKey={activeKey}
                      onSelect={(key: string | string[] | null) => setActiveKey(String(key))}
                    >
                      {values.groups.map((group, index) => (
                        <StyledCard key={index}>
                          <StyledAccordionToggle isEmpty={isGroupEmpty(group)} eventKey={index.toString()}>
                            <StyledAccordionToggleHeader>
                              <StyledFormHeader>Solar panel group {index + 1}</StyledFormHeader>
                              {activeKey !== index.toString() && !isGroupEmpty(group) && (
                                <StyledChangeButton
                                  variant="link"
                                  onClick={(e: MouseEvent) => {
                                    e.stopPropagation();
                                    setActiveKey(index.toString());
                                  }}
                                >
                                  Change
                                </StyledChangeButton>
                              )}
                            </StyledAccordionToggleHeader>
                            {isGroupComplete(group) && activeKey !== index.toString() && (
                              <p className="mb-0">
                                {orientationOptions[Number(group.orientation)]} | {tiltOptions[Number(group.tilt)]} |{' '}
                                {group.panelCount} panels
                              </p>
                            )}
                          </StyledAccordionToggle>
                          <Accordion.Collapse eventKey={index.toString()}>
                            <StyledCardBody>
                              <Field name={`groups.${index}.orientation`}>
                                {({ field, meta }: FieldProps<string>) => (
                                  <Form.Group controlId={`orientation-${index}`} className="text-left">
                                    <FieldLabelWrapper>
                                      <StyledFormLabel>Orientation of panels</StyledFormLabel>
                                      <InfoDiv
                                        onClick={(e) =>
                                          showInfoModal(
                                            e,
                                            <StyledModalContent>
                                              <h3>Direction of solar panels in a group</h3>
                                              <p>
                                                Please select the direction in which this group of solar panels faces.{' '}
                                                <br />
                                                In the example below, Group 1 faces west, Group 2 faces south, and Group
                                                3 faces east.
                                              </p>
                                              <img src={modalDirection} alt="Direction of solar panels in a group" />
                                            </StyledModalContent>
                                          )
                                        }
                                      >
                                        <img
                                          style={{
                                            width: '20px',
                                            height: '20px',
                                          }}
                                          src={theme.icons.help}
                                          alt="Info"
                                        />
                                      </InfoDiv>
                                    </FieldLabelWrapper>
                                    <Form.Control as="select" {...field} className={cx({ error: !!meta.error })}>
                                      <option value="">Select orientation</option>
                                      {Object.entries(orientationOptions).map(([value, label]) => (
                                        <option key={value} value={value}>
                                          {label}
                                        </option>
                                      ))}
                                    </Form.Control>
                                    {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                                  </Form.Group>
                                )}
                              </Field>

                              <Field name={`groups.${index}.tilt`}>
                                {({ field, meta }: FieldProps<string>) => (
                                  <Form.Group controlId={`tilt-${index}`} className="text-left">
                                    <FieldLabelWrapper>
                                      <StyledFormLabel>Angle of panels</StyledFormLabel>
                                      <InfoDiv
                                        onClick={(e) =>
                                          showInfoModal(
                                            e,
                                            <StyledModalContent>
                                              <h3>Angle of solar panels in a group</h3>
                                              <p>
                                                Please select the angle that closest matches your roof section and solar
                                                panels. Select <strong>40°</strong> if you are unsure, as this is the
                                                typical for a UK roof. Examples of the roof angles are shown below:
                                              </p>
                                              <img src={modalAngle} alt="Angle of solar panels in a group" />
                                            </StyledModalContent>
                                          )
                                        }
                                      >
                                        <img
                                          style={{
                                            width: '20px',
                                            height: '20px',
                                          }}
                                          src={theme.icons.help}
                                          alt="Info"
                                        />
                                      </InfoDiv>
                                    </FieldLabelWrapper>
                                    <Form.Control as="select" {...field} className={cx({ error: !!meta.error })}>
                                      <option value="">Select tilt</option>
                                      {Object.entries(tiltOptions).map(([value, label]) => (
                                        <option key={value} value={value}>
                                          {label}
                                        </option>
                                      ))}
                                    </Form.Control>
                                    {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                                  </Form.Group>
                                )}
                              </Field>

                              <Field name={`groups.${index}.panelCount`}>
                                {({ field, meta }: FieldProps<string>) => (
                                  <Form.Group controlId={`panelCount-${index}`} className="text-left">
                                    <FieldLabelWrapper>
                                      <StyledFormLabel>Number of panels</StyledFormLabel>
                                      <InfoDiv
                                        onClick={(e) =>
                                          showInfoModal(
                                            e,
                                            <StyledModalContent>
                                              <h3>Number of solar panels in a group</h3>
                                              <p>
                                                How many solar panels are in this specific group? In the example below,
                                                there are 4 panels in Group 1, 11 panels in Group 2, and 4 panels in
                                                Group 3.
                                              </p>
                                              <img src={modalNumber} alt="Number of solar panels in a group" />
                                            </StyledModalContent>
                                          )
                                        }
                                      >
                                        <img
                                          style={{
                                            width: '20px',
                                            height: '20px',
                                          }}
                                          src={theme.icons.help}
                                          alt="Info"
                                        />
                                      </InfoDiv>
                                    </FieldLabelWrapper>
                                    <Form.Control
                                      type="number"
                                      min="1"
                                      max="100"
                                      {...field}
                                      placeholder="Enter number of panels"
                                      className={cx({ error: !!meta.error })}
                                    />
                                    {meta.error && meta.touched && <ErrorLabel label={meta.error} />}
                                  </Form.Group>
                                )}
                              </Field>
                            </StyledCardBody>
                          </Accordion.Collapse>
                        </StyledCard>
                      ))}
                    </StyledAccordion>
                  )}
                </FieldArray>

                {totalPanelsDifferentToNumPanels(values?.groups) && (
                  <ErrorLabel
                    label={`You earlier said you had ${numPanels} panels but the total entered here is ${totalPanelsFilled(
                      values?.groups
                    )}. If ${totalPanelsFilled(
                      values?.groups
                    )} is correct press Continue. Otherwise edit the numbers entered above.`}
                  />
                )}

                <Form.Group>
                  {!isSubmitting ? (
                    <Button variant="primary" disabled={!isValid || isSubmitting} className="w-100" type="submit">
                      Continue
                    </Button>
                  ) : (
                    <LoopSpinner />
                  )}
                </Form.Group>
              </Form>
            )}
          </Formik>
        </StyledFormContainer>
      </div>

      <InfoModal show={showInfo} onClose={() => setShowInfo(false)} infoContent={infoContent} />
    </>
  );
};

export default SolarPanelGroupsContainer;
